import { useState, useEffect } from "react";
import { format } from "date-fns";
import { CheckCircle2, Trash2, Pencil } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function ActivityDetailDialog({ activity, open, onOpenChange, onUpdate, onDelete, onComplete }) {
  const [editing, setEditing] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [notes, setNotes] = useState("");
  const [form, setForm] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    if (activity) {
      const dt = new Date(activity.scheduled_at);
      setForm({ business_name: activity.business_name, result: activity.result || "",
        date: format(dt, "yyyy-MM-dd"), time: format(dt, "HH:mm") });
    }
    setEditing(false); setCompleting(false); setNotes("");
  }, [activity]);

  if (!activity || !form) return null;
  const isPlanned = activity.status === "Planned";

  const save = async () => {
    await onUpdate(activity.id, {
      business_name: form.business_name, result: form.result || null,
      scheduled_at: new Date(`${form.date}T${form.time}:00`).toISOString(),
    });
    setEditing(false);
  };
  const doComplete = async () => { await onComplete(activity.id, notes); setCompleting(false); onOpenChange(false); };
  const remove = async () => { await onDelete(activity.id); setConfirmDelete(false); onOpenChange(false); };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[480px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {activity.business_name}
              <Badge variant={isPlanned ? "outline" : "default"} className={isPlanned ? "" : "bg-success text-success-foreground"}>{activity.status}</Badge>
            </DialogTitle>
            <DialogDescription>{format(new Date(activity.scheduled_at), "EEEE, MMM d, yyyy 'at' h:mm a")}</DialogDescription>
          </DialogHeader>

          {editing ? (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Business Name</Label>
                <Input value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Date</Label>
                  <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
                <div className="space-y-2"><Label>Time</Label>
                  <Input type="time" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Result / Notes</Label>
                <Textarea value={form.result} onChange={(e) => setForm({ ...form, result: e.target.value })} rows={2} /></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditing(false)}>Cancel</Button>
                <Button onClick={save} data-testid="save-activity-edit">Save</Button>
              </DialogFooter>
            </div>
          ) : completing ? (
            <div className="space-y-4">
              <div className="space-y-2"><Label>Completion Notes</Label>
                <Textarea value={notes} data-testid="completion-notes" onChange={(e) => setNotes(e.target.value)} rows={3} placeholder="How did it go?" /></div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setCompleting(false)}>Cancel</Button>
                <Button onClick={doComplete} data-testid="confirm-complete" className="bg-success text-success-foreground hover:bg-success/90">Mark Completed</Button>
              </DialogFooter>
            </div>
          ) : (
            <div className="space-y-3">
              {activity.actions_taken?.length > 0 && <div className="text-sm"><span className="font-medium">Actions:</span> {activity.actions_taken.join(", ")}</div>}
              {activity.offers_given?.length > 0 && <div className="text-sm"><span className="font-medium">Offers:</span> {activity.offers_given.join(", ")}</div>}
              {activity.result && <div className="text-sm p-2 bg-secondary rounded"><span className="font-medium">Result:</span> {activity.result}</div>}
              {activity.completion_notes && <div className="text-sm p-2 bg-success/10 rounded"><span className="font-medium">Completion:</span> {activity.completion_notes}</div>}
              <DialogFooter className="gap-2 flex-wrap">
                <Button variant="outline" className="text-destructive" onClick={() => setConfirmDelete(true)} data-testid="delete-activity"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>
                <Button variant="outline" onClick={() => setEditing(true)} data-testid="edit-activity"><Pencil className="h-4 w-4 mr-1" /> Edit</Button>
                {isPlanned && <Button onClick={() => setCompleting(true)} data-testid="complete-activity" className="bg-success text-success-foreground hover:bg-success/90"><CheckCircle2 className="h-4 w-4 mr-1" /> Complete</Button>}
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this activity?</AlertDialogTitle>
            <AlertDialogDescription>This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid="confirm-delete-activity" className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
